import { ParamMap } from '@angular/router';
import { SelectSearchValue } from '@domains/search-engine/search-engine-fields/select-search/select-search.component';

export enum SearchEngineFieldType {
  Dropdown = 'dropdown',
  TextInput = 'textInput',
  LocationPoleEmploi = 'locationPoleEmploi',
  LocationEures = 'locationEures',
  Location = 'location',
  Address = 'address',
  Select = 'select',
  Checkbox = 'checkbox',
  DateInput = 'dateInput',
  DurationSelect = 'durationSelect',
  WeeklyDurationSelect = 'weeklyDurationSelect',
  RomesInput = 'romesInput',
  TextRomesInput = 'textRomesInput',
  JobsEuresInput = 'jobsEuresInput',
}

export enum SearchEngineQueryParams {
  Location = 'location',
  Department = 'departement',
  Region = 'region',
  City = 'commune',
  CityPlaceId = 'cityPlaceId',
  Address = 'address',
  Distance = 'distance',
  From = 'de',
  To = 'a',
  Job = 'metier',
}

export interface SearchEngineFields {
  mainField: SearchEngineField;
  submainFields: SearchEngineField[];
  advancedFields?: SearchEngineField[];
  advancedFieldsWidth?: string;
  redirectUri: string;
  trackEventName: string;
  liveSearch: boolean;
  staticQueryParams?: any;
}

export interface FieldInputConfig {
  name: string;
  placeholder: string;
  ariaLabel: string;
  icon?: string;
}

export type StaticNameFieldInputConfig = Omit<FieldInputConfig, 'name'>;

export interface FieldStyleConfig {
  flexValue?: number;
}

export interface FieldStyleConfigWithWidth extends FieldStyleConfig {
  percentageWidth?: number;
}

export interface FieldExtraConfig {
  required?: boolean;
}

export interface FieldExtraConfigWithValue<T> extends FieldExtraConfig {
  value: T;
}

export abstract class SearchEngineField<T = unknown> {
  name: string;
  placeholder: string;
  icon?: string;
  type!: SearchEngineFieldType;
  flexValue: number;
  ariaLabel: string;
  required: boolean;

  static defaultPercentageWidth = 80;

  constructor(inputConfig: FieldInputConfig, extraConfig?: FieldExtraConfig, inputStyle?: FieldStyleConfig) {
    const { name, placeholder, ariaLabel, icon } = inputConfig;
    this.name = name;
    this.placeholder = placeholder;
    this.ariaLabel = ariaLabel;
    this.icon = icon;
    this.flexValue = inputStyle?.flexValue ?? 1;
    this.required = extraConfig?.required ?? false;
  }

  valueToQueryParams(value: T): Record<string, unknown> {
    if (value) {
      return { [this.name]: value };
    }
    return {};
  }

  paramMapToValue(paramMap: ParamMap): T | string | null {
    return paramMap.get(this.name);
  }
}

export abstract class FieldWithValueWidth<T = unknown> extends SearchEngineField<T> {
  value: T;
  percentageWidth: number;

  constructor(
    inputConfig: FieldInputConfig,
    extraConfig: FieldExtraConfigWithValue<T>,
    inputStyle?: FieldStyleConfigWithWidth
  ) {
    super(inputConfig, extraConfig, inputStyle);
    this.percentageWidth = inputStyle?.percentageWidth ?? SearchEngineField.defaultPercentageWidth;
    this.value = extraConfig.value;
  }
}

export class SearchEngineUtility {
  static buildSelectSearchValue(entries: Record<string, string>): SelectSearchValue[] {
    const selectSearchValues: SelectSearchValue[] = [];
    Object.entries(entries).forEach(([key, value]) => {
      selectSearchValues.push({ name: value, paramValue: key });
    });
    return selectSearchValues;
  }
}
